import { Grid, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusBadge } from "@smartrr/shared/customerPortal/components/AquariusBadge";
import { BigIntString } from "@smartrr/shared/entities/BigIntString";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import { ISmartrrOrderFromDb } from "@smartrr/shared/entities/Order";
import { collapseProducts } from "@smartrr/shared/utils/collapseProducts";
import { formatMoney } from "@smartrr/shared/utils/formatMoney";
import { localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import groupBy from 'lodash/groupBy';
import React, { useEffect, useMemo, useRef, useState } from "react";

import { AquariusBorderedCard } from "@customer-app/account/views/OperationAquarius/components/AquarisBorderedCard";
import { HiddenProductsNumber } from "@customer-app/account/views/OperationAquarius/components/HiddenProductsNumber";

import { useMobileQuery } from "../../../../utils";
import { OrderItemImage } from "../../Subscription/constants";
import { BadgeContainer } from "../components";
import { renderOrderIcon, renderOrderStatus } from "../utils";
import { isCPSPrepaid } from "@smartrr/shared/utils/isPrepaid";

interface OrderCardProps {
  order: ISmartrrOrderFromDb;
  onClick: () => void;
}

export const OrderCard = ({ order, onClick }: OrderCardProps) => {
  const isMobile = useMobileQuery();
  const totalAfterDiscounts = BigIntString.fromBigIntString(order.totalNetBeforeRefunds);
  const [numOfHidden, setNumOfHidden] = useState<number | null>();

  const lengthOfItemsGroupedBySubscriptionsId = Object.keys(
    groupBy(order.items, ({ shopifySubscriptionId }) => shopifySubscriptionId)
  ).length;

  const isOrderPrepaid = useMemo(() => {
    return order.sts.some(sts => isCPSPrepaid(sts.schedule)) && totalAfterDiscounts === 0;
  }, [order]);

  const subscription = useMemo(
    () => order.sts.find(st => st.shopifyId === order.items[0].shopifySubscriptionId),
    [order]
  );

  const gridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setNumOfHidden(collapseProducts(gridRef, isMobile, "small", 5));
  }, [gridRef, isMobile]);

  return (
    <div
      onClick={onClick}
      data-smartrr-order-id={order.shopifyId}
      className={prefixClassName("order-history-card")}
    >
      <AquariusBorderedCard isMobile={isMobile}>
        <Stack
          spacing={1}
          justifyContent="space-between"
          height="100%"
          className={prefixClassName("ohc-inner-container")}
        >
          <Stack direction="row" spacing="7px" alignItems="center" width="100%">
            <BadgeContainer className={prefixClassName("ohc-info-status")}>
              <Typography variant="caption1" className={prefixClassName("order-date")}>
                {localize("utils.dates.toViewDateLong", {
                  date: ISODateString.fromString(order.orderProcessedDate).toJSDate(),
                })}
              </Typography>
              {order.sts.reduce((acc, item) => (acc += item?.gifts?.length ?? 0), 0) ===
              lengthOfItemsGroupedBySubscriptionsId ? (
                <AquariusBadge icon="gift" label={localize("orderHistory.gifted")} />
              ) : order.sts.filter(item => !!item.gifts?.length).length ? (
                <AquariusBadge icon="gift" label={localize("orderHistory.partiallyGifted")} />
              ) : null}
              <AquariusBadge
                icon={renderOrderIcon(order, subscription?.deliveryMethod)}
                label={renderOrderStatus(order, subscription?.deliveryMethod)}
              />
            </BadgeContainer>
          </Stack>
          <Grid
            className={prefixClassName("ohc-cards-container")}
            container
            ref={gridRef}
            spacing={"7px"}
            sx={{
              alignItems: "center",
              width: "max-content",
              position: "relative",
              "& :first-of-type": {
                paddingLeft: 0,
              },
            }}
          >
            {order.items.map(item => {
              return (
                <Grid
                  item
                  key={item.id}
                  sx={{ width: "max-content" }}
                  className={prefixClassName("ohc-card-item")}
                >
                  <OrderItemImage
                    purchasableVariant={item.vnt}
                    quantity={item.quantity}
                    size="small"
                    className={prefixClassName("ohc-cards-container")}
                  />
                </Grid>
              );
            })}
            {!!numOfHidden && (
              <HiddenProductsNumber
                isMobile={isMobile}
                imageSize="small"
                numOfHidden={numOfHidden}
                sx={{ top: "17px !important", right: "-15px !important" }}
              />
            )}
          </Grid>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption1" className={prefixClassName("product-count")}>
              {localize("general.itemCount", {
                count: +order.items.reduce((acc, prev) => (acc += prev.quantity), 0),
              })}
            </Typography>
            {isOrderPrepaid ? (
              <Typography variant="caption2" className={prefixClassName("product-total")}>
                {localize("general.prepaid")}
              </Typography>
            ) : (
              <Typography variant="caption2" className={prefixClassName("product-total")}>
                {formatMoney(totalAfterDiscounts ?? 0, order.orderCurrency)}
              </Typography>
            )}
          </Stack>
        </Stack>
      </AquariusBorderedCard>
    </div>
  );
};
